import { useCallback, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { trackBookAppointmentCompleted } from "../lib/analytics";

const INITIAL_FORM = {
  reason: "",
  preferred_date: "",
  preferred_time: "",
  name: "",
  phone: "",
  email: "",
  message: "",
};

/**
 * useBookingFlow — encapsulates state + validation for the 3-step appointment wizard.
 * Owns: step navigation, form state, submission.
 */
export function useBookingFlow() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  const canGoNext = () => {
    if (step === 1) return !!form.reason;
    if (step === 2) return !!(form.preferred_date && form.preferred_time);
    return true;
  };

  const next = () => { if (canGoNext()) setStep((s) => Math.min(3, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const isValidContact = () => form.name.trim() && form.phone.trim();

  const submit = async () => {
    if (!isValidContact()) {
      toast.error("Please fill your name and mobile number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", form);
      trackBookAppointmentCompleted({
        reason: form.reason,
        has_email: !!form.email,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return { step, form, set, loading, done, canGoNext, next, back, submit };
}
